import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DesktopLayout from '../../components/layout/DesktopLayout';
import WishFilterBar from '../../components/wishes/WishFilterBar';
import WishCardList from '../../components/wishes/WishCardList';
import WishDetailModal from '../../components/wishes/WishDetailModal/WishDetailModal';
import WishFormModal from '../../components/wishes/WishFormModal';
import { getWishes } from '../../services/wishesApi';
import type { Wish } from '../../types';
import './WishListPage.css';

const WishListPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [urlSearchParams] = useSearchParams();
  const [wishes, setWishes] = useState<Wish[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<{
    category?: string;
    region?: string;
    urgent?: boolean;
    search?: string;
  }>({});

  // Modal state (desktop only)
  const [showWishDetailModal, setShowWishDetailModal] = useState(false);
  const [showWishFormModal, setShowWishFormModal] = useState(false);
  const [selectedWishId, setSelectedWishId] = useState<string | null>(null);

  // Get all wish IDs for navigation
  const allWishIds = wishes.map(wish => wish.id);

  const categories = ['維修', '保養', '美容', '改裝'];
  const regions = ['台北市', '新北市', '桃園市', '台中市', '台南市', '高雄市'];

  useEffect(() => {
    const fetchWishes = async () => {
      setLoading(true);
      try {
        const result = await getWishes(filters);
        setWishes(result.data);
      } catch (error) {
        console.error('Failed to fetch wishes:', error);
        setWishes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchWishes();
  }, [filters]);

  // Open modal if id is provided in query string
  useEffect(() => {
    const wishId = urlSearchParams.get('id');
    if (wishId && window.innerWidth >= 768) {
      setSelectedWishId(wishId);
      setShowWishDetailModal(true);
      // Clear the id from URL to prevent reopening on refresh
      const params = new URLSearchParams(urlSearchParams);
      params.delete('id');
      const queryString = params.toString();
      const newUrl = queryString ? `/wishes?${queryString}` : '/wishes';
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  const handleFilterChange = (newFilters: {
    category?: string;
    region?: string;
    urgent?: boolean;
    search?: string;
  }) => {
    setFilters(newFilters);
  };

  const handleWishClick = (wishId: string) => {
    // Desktop: open modal, Mobile: navigate
    if (window.innerWidth >= 768) {
      setSelectedWishId(wishId);
      setShowWishDetailModal(true);
    } else {
      navigate(`/wishes/${wishId}`);
    }
  };

  const handleCloseWishDetailModal = () => {
    setShowWishDetailModal(false);
    setSelectedWishId(null);
  };

  const handleWishChange = (wishId: string) => {
    setSelectedWishId(wishId);
  };

  const handleCloseWishFormModal = () => {
    setShowWishFormModal(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleWishFormSuccess = async (_wishId: string) => {
    // Refresh the wishes list
    const result = await getWishes(filters);
    setWishes(result.data);
  };

  const handleOpenWishForm = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setShowWishFormModal(true);
  };

  return (
    <div className="wish-list-page">
      <DesktopLayout>
        <main className="wish-list-page__main">
          <div className="wish-list-page__container">
            <div className="wish-list-page__header">
              <h1 className="wish-list-page__title">許願池</h1>
              <p className="wish-list-page__subtitle">
                發布您的需求，讓專業服務商來為您服務
              </p>
              <button
                className="wish-list-page__create-btn"
                onClick={handleOpenWishForm}
              >
                ✨ 我要許願
              </button>
            </div>

            <WishFilterBar
              onFilterChange={handleFilterChange}
              categories={categories}
              regions={regions}
            />

            <WishCardList wishes={wishes} loading={loading} onWishClick={handleWishClick} />
          </div>
        </main>
      </DesktopLayout>

      {/* WishDetail Modal (Desktop) */}
      <WishDetailModal
        isOpen={showWishDetailModal}
        onClose={handleCloseWishDetailModal}
        wishId={selectedWishId}
        allWishIds={allWishIds}
        onWishChange={handleWishChange}
      />

      {/* WishForm Modal (Desktop) */}
      <WishFormModal
        isOpen={showWishFormModal}
        onClose={handleCloseWishFormModal}
        onSuccess={handleWishFormSuccess}
      />
    </div>
  );
};

export default WishListPage;
